import { RootState } from 'store';
import { SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';

import {
  CustomTD,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from 'shared/components/Table';

import { lynch, manatee } from 'shared/config/Colors';
import { Spacer } from 'shared/components/Layout';
import { ConfirmationModal } from 'shared/components/ConfirmationModal';
import { Modal } from 'shared/components/Modal';
import { ModalWrapper } from 'shared/components/ModalWrapper';
import { P } from 'shared/components/Typography';
import { SortEnums } from 'shared/enum/sortEnums';
import { TabbableButton } from 'shared/components/Button';
import { TableColumnIdEnums } from 'shared/enum/tableEnums';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import SVG from 'shared/components/SVG';

import { toast } from 'react-toastify';

import {
  deleteDealershipCustomAddress,
  getDealershipCustomAddresses,
  sortDealershipCustomAddresses,
} from '../store/dealershipActions';

import { CustomAddressForm } from './CustomAddressForm';

export const DealershipCustomAddressTable = () => {
  const dispatch = useAppDispatch();

  const [sortDir, setSortDir] = useState(SortEnums.ASC);
  const [sortColumn, setSortColumn] = useState(TableColumnIdEnums.CITY);

  const customAddresses = useSelector(
    (state: RootState) => state.dealershipReducer.customAddresses
  );

  const dealership = useSelector(
    (state: RootState) => state.dealershipReducer.dealership
  );

  const onSortHandle = (columnName: SetStateAction<TableColumnIdEnums>) => {
    setSortColumn(columnName);

    if (columnName === sortColumn && sortDir === SortEnums.ASC) {
      setSortDir(SortEnums.DESC);
    } else {
      setSortDir(SortEnums.ASC);
    }
  };

  const onSuccessGetDealershipCustomAddresses = () => {
    dispatch(
      sortDealershipCustomAddresses({
        sortColumn,
        asc: sortDir === SortEnums.ASC,
      })
    );
  };

  const getDealershipAddresses = () => {
    if (dealership) {
      dispatch(
        getDealershipCustomAddresses(
          dealership.id,
          onSuccessGetDealershipCustomAddresses
        )
      );
    }
  };

  const onDeleteCustomAddress = (
    addressId: string,
    close: () => void,
    setLoading: (isLoading: boolean) => void
  ) => {
    dispatch(
      deleteDealershipCustomAddress(
        { dealershipId: dealership!.id, addressId },
        () => {
          setLoading(false);
          close();
          toast.success('Custom shipping address was deleted successfully.');
        },
        () => {
          setLoading(false);
          close();
          toast.error(
            "Couldn't delete the shipping address. Please try again or contact support."
          );
        }
      )
    );
  };

  // Getting addresses for the first time
  useEffect(() => {
    if (dealership) {
      getDealershipAddresses();
    }
  }, [dealership]);

  // Handle sorting after clicking a sort handle
  useEffect(() => {
    dispatch(
      sortDealershipCustomAddresses({
        sortColumn,
        asc: sortDir === SortEnums.ASC,
      })
    );
  }, [sortColumn, sortDir]);

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>SHIP TO NAME</Th>
          <Th>STREET ADDRESS</Th>
          <Th>ADDRESS LINE 2</Th>
          <Th
            data-direction={sortDir}
            data-active={sortColumn === TableColumnIdEnums.CITY}
            onClick={() => onSortHandle(TableColumnIdEnums.CITY)}
          >
            CITY
          </Th>
          <Th>STATE</Th>
          <Th
            data-direction={sortDir}
            data-active={sortColumn === TableColumnIdEnums.ZIPCODE}
            onClick={() => onSortHandle(TableColumnIdEnums.ZIPCODE)}
          >
            ZIP
          </Th>
          <Th>COUNTRY</Th>
          <Th>PHONE NO</Th>
        </Tr>
      </Thead>
      <Tbody>
        {customAddresses?.map((address) => (
          <Tr key={address?.id}>
            <Td>{address.shipTo}</Td>
            <Td>{address.street}</Td>
            <Td>{address.number}</Td>
            <Td>{address.city}</Td>
            <Td>{address.state}</Td>
            <Td>{address.zipCode}</Td>
            <Td>{address.country}</Td>
            <Td>{address.phoneNumber}</Td>
            <Td>
              <CustomTD flex center>
                <ModalWrapper
                  trigger={
                    <TabbableButton>
                      <SVG
                        icon={<EditIcon />}
                        color={manatee}
                        hoverColor={lynch}
                      />
                    </TabbableButton>
                  }
                  modal
                  closeOnEscape={false}
                >
                  {(close: () => void) => (
                    <Modal title="Edit address" close={close} withoutFooter={1}>
                      <CustomAddressForm
                        onClose={() => {
                          close();
                          getDealershipAddresses();
                        }}
                        editMode
                        data={address}
                      />
                    </Modal>
                  )}
                </ModalWrapper>
                <Spacer w="16px" />
                <ConfirmationModal
                  trigger={
                    <TabbableButton>
                      <SVG
                        icon={<TrashIcon />}
                        color={manatee}
                        hoverColor={lynch}
                      />
                    </TabbableButton>
                  }
                  title="Delete Custom Shipping address?"
                  htmlMessage="Are you sure you want to delete this Custom Shipping address?<br /><br /><b>Note:</b> Existing orders with this shipping address will not be deleted or disrupted."
                  buttonText="Delete"
                  cancelButtonText="Cancel"
                  bottomSpacing={40}
                  confirm={(close, setLoading) =>
                    onDeleteCustomAddress(address.id, close, setLoading)
                  }
                />
              </CustomTD>
            </Td>
          </Tr>
        ))}

        {!customAddresses?.length && (
          <Tr>
            <Td colSpan={9}>
              <P>No Custom shipping addresses yet.</P>
            </Td>
          </Tr>
        )}
      </Tbody>
    </Table>
  );
};
